import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { useAuth0 } from '@auth0/auth0-react';
import { CircularProgress } from '@mui/material';

import { useFeatures } from 'hooks/useFeatures';
import { Welcome } from 'pages/Welcome/Welcome';
import PageWrapper from 'shared/Wrappers/PageWrapper/PageWrapper';

import classes from './AppRouter.module.scss';

// Route-level code-splitting: each page loads as its own chunk on first visit (the Suspense
// boundary below shows the same centered spinner meanwhile; Header/Footer live in App.tsx and
// stay mounted). Import the DEEPEST per-page file — the 'pages' barrel (and the Profile/Checkout
// sub-barrels) re-export everything, so importing them would pull every page into one chunk.
// React.lazy needs a default export, so named page exports are mapped via .then().
const lazyNamed = <T extends Record<string, any>>(importer: () => Promise<T>, name: keyof T) =>
  lazy(async () => {
    const m = await importer();
    return { default: m[name] };
  });

const Dashboard = lazyNamed(async () => import('pages/Dashboard/Dashboard'), 'Dashboard');
const MyWallets = lazyNamed(async () => import('pages/MyWallets/MyWallets'), 'MyWallets');
const InvoicesPage = lazy(async () => import('pages/Invoices/Invoices.page'));
const BillingPage = lazy(async () => import('pages/Billing/Billing.page'));
const TasksPage = lazy(async () => import('pages/Tasks/Tasks.page'));
const ContractsPage = lazy(async () => import('pages/Contracts/Contracts.page'));
const Subscriptions = lazyNamed(async () => import('pages/Subscriptions/Subscriptions.page'), 'Subscriptions');
const BuyAndHost = lazyNamed(async () => import('pages/BuyAndHost/BuyAndHost'), 'BuyAndHost');
const HostedMiners = lazyNamed(async () => import('pages/HostedMiners/HostedMiners'), 'HostedMiners');
const AddMiner = lazyNamed(async () => import('pages/AddMiner/AddMiner'), 'AddMiner');
const Servers = lazyNamed(async () => import('pages/Servers/Servers'), 'Servers');
const Pools = lazyNamed(async () => import('pages/Profile/Pools/Pools.page'), 'Pools');
const ProfileLayout = lazyNamed(async () => import('pages/Profile/Profile.layout'), 'ProfileLayout');
const ProfileSettingsPage = lazyNamed(
  async () => import('pages/Profile/ProfileSettings/ProfileSettings.page'),
  'ProfileSettingsPage',
);
const AddressBookPage = lazyNamed(async () => import('pages/Profile/AddressBook/AddressBook.page'), 'AddressBookPage');
const SecurityPage = lazyNamed(async () => import('pages/Profile/Security/Security.page'), 'SecurityPage');
const Failed = lazyNamed(async () => import('pages/Checkout/components/Failed/Failed'), 'Failed');
const Success = lazyNamed(async () => import('pages/Checkout/components/Success/Success'), 'Success');

type ProtectedRouteProps = {
  isAllowed?: boolean;
  redirectTo?: string;
};

const ProtectedRoute = ({ isAllowed = false, redirectTo = '/' }: ProtectedRouteProps) => {
  if (!isAllowed) {
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
};
const AppRouter = () => {
  const { isAuthenticated, isLoading } = useAuth0();
  // Plan 06: mining defaults visible (guards default-visible while the flags fetch is in
  // flight/before login); colo is opt-in. Wrapping happens only at this routing layer — no
  // pre-existing mining page component below is touched.
  const { miningEnabled, coloEnabled } = useFeatures();
  const AuthenticatedRoutes = (
    <Route element={<ProtectedRoute isAllowed={isAuthenticated} />}>
      {/* Nested */}
      <Route element={<ProfileLayout />}>
        <Route path='/profile-settings' element={<ProfileSettingsPage />} />
        <Route element={<ProtectedRoute isAllowed={miningEnabled} />}>
          <Route path='/pools' element={<Pools />} />
        </Route>
        <Route path='/address-book' element={<AddressBookPage />} />
        <Route path='/security' element={<SecurityPage />} />
        {/* <Route path='/subscription' element={<SubscriptionPage />} /> */}
      </Route>
      <Route path='checkout'>
        {/* <Route element={<Checkout />} index /> */}
        <Route path='success' element={<Success />} />
        <Route path='failed' element={<Failed />} />
      </Route>

      {/* root */}
      {/* <Route path='my-cards' element={<MyCardsPage />} /> */}
      <Route path='invoices' element={<InvoicesPage />} />
      <Route path='billing' element={<BillingPage />} />
      <Route path='tasks' element={<TasksPage />} />
      <Route path='contracts' element={<ContractsPage />} />
      <Route path='subscriptions' element={<Subscriptions />} />
      <Route path='dashboard' element={<Dashboard />} />
      {/* <Route path='tax-reporting' element={<TaxReportingPage />} /> */}

      {/* Wallets page (Plan 06 §7): shared balance/top-up/transactions ledger needed by both
          mining and colo orgs (colo billing). Not route-gated — BTC-specific pieces inside
          the page are guarded individually with the mining flag instead. */}
      <Route path='my-wallets' element={<MyWallets />} />

      {/* mining guard: hashrate charts, BTC earnings (Plan 06 §My-portal changes) */}
      <Route element={<ProtectedRoute isAllowed={miningEnabled} />}>
        <Route path='buy-and-host' element={<BuyAndHost />} />
        <Route path='hosted-miners' element={<HostedMiners />} />
        <Route path='shop' element={<AddMiner />} />
        {/* <Route path='buy-hosted-miners' element={<AddMiner type={Type.buyHostedMiners} />} /> */}
      </Route>

      {/* colo guard: "Servers" consumption view with per-row confidence badges */}
      <Route element={<ProtectedRoute isAllowed={coloEnabled} />}>
        <Route path='servers' element={<Servers />} />
      </Route>
    </Route>
  );

  if (isLoading) {
    return (
      <div className={classes.loaderContainer}>
        <CircularProgress size={40} sx={{ transform: 'translate(-50%, -50%)' }} />
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div className={classes.loaderContainer}>
          <CircularProgress size={40} sx={{ transform: 'translate(-50%, -50%)' }} />
        </div>
      }
    >
      <Routes>
        <Route path='/' element={<PageWrapper />}>
          <Route index element={isAuthenticated ? <Dashboard /> : <Welcome />} />
          {/* <Route path='login' element={<Login />} /> */}
          {/* Routes for only authenticated users */}
          {AuthenticatedRoutes}
          {/* Replace home is route is incorrect */}
          {/* <Route path='/jwt' element={<JwtPage />} /> */}
          {/* <Route path='*' element={<Navigate to='/' replace />} /> */}
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
